<template>
    <div>
        <ValidationObserver v-slot="{ dirty }" ref="observer">
            <form class="grid grid-cols-2">
                <app-fieldset class="col-span-2 mt-4" :label="$t('commons.infos')">
                    <app-input-text
                        :label="$t('deductions.object')"
                        v-model="deduction.object"
                        class="col-span-2 md:col-span-1"
                        :required="true"
                        @blur="save()"
                        ref="objectInput"
                    />
                    <app-input-text
                        :label="$t('deductions.code')"
                        v-model="deduction.code"
                        class="col-span-2 md:col-span-1"
                        @blur="save()"
                    />
                    <app-select
                        :label="$t('deductions.type')"
                        v-model="deduction.type"
                        class="col-span-2"
                        @change="saveType"
                    >
                        <option value="caution">{{ $t('deductions.types.caution') }}</option>
                        <option value="garanteeDeduction">{{ $t('deductions.types.garanteeDeduction') }}</option>
                        <option value="penalty">{{ $t('deductions.types.penalty') }}</option>
                        <option value="deduction">{{ $t('deductions.types.deduction') }}</option>
                    </app-select>
                    <app-bundle-picker
                        class="col-span-2"
                        v-model="deduction.bundle"
                        :options="bundles"
                        :label="$t('deductions.bundle')"
                        :companyLabel="$t('commons.company')"
                        @input="onBundleChange"
                    ></app-bundle-picker>
                    <app-number-input
                        :label="$t('commons.amountTTC')"
                        v-model="deduction.amountTTC"
                        :allowsNegative="true"
                        @blur="save()"
                        :currency="currencyFormat.currency"
                    />
                    <app-select :label="$t('commons.status')" v-model="deduction.deductionStatus" @input="save()">
                        <option value=""></option>
                        <option value="provisional">{{ $t('deductions.status.provisional') }}</option>
                        <option value="definitive">{{ $t('deductions.status.definitive') }}</option>
                        <option value="obsolete">{{ $t('deductions.status.obsolete') }}</option>
                    </app-select>
                </app-fieldset>
                <app-fieldset class="col-span-2 mt-5" :label="$t('commons.dates')">
                    <app-date-input
                        class="col-span-2 md:col-span-1"
                        v-model="deduction.emissionDate"
                        :label="$t('deductions.emissionDate')"
                        @input="save()"
                    />
                </app-fieldset>
                <app-save-on-leave :dirty="dirty" :saveFn="() => save(true)"></app-save-on-leave>
            </form>
        </ValidationObserver>
    </div>
</template>

<script>
import { getMapById, sanitize } from '@/services/sanitize.service';
import { getDeduction, updateDeduction } from './deductions.service';
import { combineLatest } from 'rxjs';
import { getBundles } from '@/features/bundles/bundles.service';
import { isMobile } from '@/state/state';
import { getCompanies } from '@/features/companies/companies.service';
import { queryProject } from '@/features/projects/projects.service';
import { getAmendments } from '@/features/amendments/amendments.service';
import AppFieldset from '@/components/appFieldset/AppFieldset.vue';
import AppInputText from '@/components/appInputText/AppInputText.vue';
import AppSelect from '@/components/appSelect/AppSelect.vue';
import AppBundlePicker from '@/components/appBundlePicker.vue';
import AppDateInput from '@/components/appDateInput/AppDateInput.vue';
import AppSaveOnLeave from '@/components/AppSaveOnLeave.vue';
import AppNumberInput from '@/components/appNumberInput/AppNumberInput.vue';

export default {
    components: {
        AppNumberInput,
        AppSaveOnLeave,
        AppDateInput,
        AppBundlePicker,
        AppSelect,
        AppInputText,
        AppFieldset,
    },
    async created() {
        queryProject(this.$route.params.projectId).then((project) => {
            this.currencyFormat.currency = project.currency;
        });
        this.init();
        localStorage.setItem(
            'deduction.lastVisitedDeductionId.' + this.$route.params.projectId,
            this.$route.params.deductionId,
        );
    },
    watch: {
        $route: function (from, to) {
            if (from.name !== to.name || from.params.deductionId !== to.params.deductionId) {
                this.focusDone = false;
            }
            this.init();
        },
    },
    methods: {
        init() {
            this.subscriptions = [
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getCompanies(this.$route.params.projectId),
                    getDeduction(this.$route.params.projectId, this.$route.params.deductionId),
                    getAmendments(this.$route.params.projectId),
                ]).subscribe(([bundles, companies, deduction, amendments]) => {
                    const marketSignedAmendments = amendments.filter(
                        (deduction) => deduction.type === 'market' && deduction.deductionStatus === 'signed',
                    );
                    const companyMap = getMapById(companies);
                    const mappedBundles = bundles.map((bundle) => ({
                        ...bundle,
                        company: companyMap[bundle.companyId],
                        marketAmount: marketSignedAmendments.reduce((acc, deduction) => {
                            if (deduction.bundleId === bundle.id) {
                                return acc + deduction.amountTTC;
                            } else {
                                return acc;
                            }
                        }, 0),
                    }));
                    this.bundleMap = getMapById(mappedBundles);
                    this.deduction = {
                        ...deduction,
                        bundle: mappedBundles.find((bundle) => bundle.id === deduction.bundleId),
                    };
                    this.bundles = mappedBundles;

                    if (!this.focusDone && !isMobile && this.deduction.name === '') {
                        this.$nextTick(() => {
                            this.$refs.objectInput.focus();
                        });
                        this.focusDone = true;
                    }
                }),
            ];
        },
        onBundleChange() {
            this.save();
        },
        sanitizeDeduction(deduction, toOmit = []) {
            return {
                ...sanitize(deduction, ['bundle', ...toOmit]),
                bundleId: deduction.bundle ? deduction.bundle.id : null,
            };
        },
        saveType() {
            this.save();
        },
        async save() {
            if (this.savingFlag) {
                return;
            } else {
                this.savingFlag = true;
            }
            try {
                const deductionEntity = this.sanitizeDeduction(this.deduction);
                await updateDeduction(this.$route.params.projectId, { id: this.deduction.id, ...deductionEntity });
                if (this.$refs.observer) {
                    this.$refs.observer.reset();
                }
            } finally {
                this.savingFlag = false;
            }
        },
    },
    data() {
        return {
            currencyFormat: {
                currency: 'EUR',
                currencyDisplay: 'symbol',
                style: 'currency',
                minimumFractionDigits: 2,
            },
            focusDone: false,
            subscriptions: [],
            bundleMap: {},
            savingFlag: false,
            bundles: [],
            deduction: {
                name: '',
                bundle: null,
            },
            project: {},
        };
    },
};
</script>
